import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import SubscribeDialog from "@/components/SubscribeDialog";
import { AlertTriangle, CheckCircle2, Clock, Loader2, Sparkles, Lock, TrendingDown } from "lucide-react";

/**
 * "Score de Salud de Tarjeta de Crédito" — compact widget.
 *
 * Fetches GET /api/accounts/{id}/credit-status and renders:
 *  - Saldo disponible / Límite (with utilization bar)
 *  - Monto "para no generar intereses" (statement balance)
 *  - Días al próximo pago + semáforo (verde/amarillo/rojo)
 *  - Próximo corte
 *
 * Semaphore logic:
 *  - red   -> days_to_payment < 3 OR utilization >= 80
 *  - amber -> days_to_payment <= 7 OR utilization >= 50
 *  - green -> otherwise
 */
export default function CreditCardHealth({ accountId, currency = "MXN", compact = false }) {
  const { isPremium } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [showSubscribe, setShowSubscribe] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .get(`/accounts/${accountId}/credit-status`)
      .then(({ data }) => {
        if (active) setStatus(data);
      })
      .catch(() => {
        if (active) setError("No se pudo cargar el estado de la tarjeta");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [accountId]);

  // Premium: fetch payment plan once status loaded & user is premium & card configured
  useEffect(() => {
    if (!isPremium || !status || !status.configured) return;
    let active = true;
    setPlanLoading(true);
    api
      .get(`/accounts/${accountId}/credit-payment-plan`)
      .then(({ data }) => {
        if (active) setPlan(data);
      })
      .catch(() => {
        if (active) setPlan(null);
      })
      .finally(() => {
        if (active) setPlanLoading(false);
      });
    return () => {
      active = false;
    };
  }, [accountId, isPremium, status]);

  if (loading) {
    return (
      <div
        className="mt-4 pt-4 border-t border-[#2A3447] flex items-center gap-2 text-[#64748B] text-xs"
        data-testid={`credit-health-loading-${accountId}`}
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Cargando salud de tarjeta...
      </div>
    );
  }

  if (error || !status) return null;

  // Not yet configured (no cutoff / payment days)
  if (!status.configured) {
    return (
      <div
        className="mt-4 pt-4 border-t border-[#2A3447] text-[11px] text-[#94A3B8] leading-relaxed"
        data-testid={`credit-health-unconfigured-${accountId}`}
      >
        Configura <span className="text-[#F1F5F9]">día de corte</span> y{" "}
        <span className="text-[#F1F5F9]">día de pago</span> para activar el score de salud.
      </div>
    );
  }

  const util = status.utilization_pct ?? 0;
  const daysPay = status.days_to_payment ?? 999;
  const interestFree = status.interest_free_amount ?? 0;

  let tone = "green";
  if (daysPay < 3 || util >= 80) tone = "red";
  else if (daysPay <= 7 || util >= 50) tone = "amber";

  const TONE = {
    green: {
      bg: "bg-[#0E2A1E]",
      border: "border-[#10B981]/40",
      text: "text-[#10B981]",
      bar: "bg-[#10B981]",
      Icon: CheckCircle2,
      label: "Sano",
    },
    amber: {
      bg: "bg-[#2A220E]",
      border: "border-[#F59E0B]/40",
      text: "text-[#F59E0B]",
      bar: "bg-[#F59E0B]",
      Icon: Clock,
      label: "Atento",
    },
    red: {
      bg: "bg-[#2A0E14]",
      border: "border-[#EF4444]/40",
      text: "text-[#EF4444]",
      bar: "bg-[#EF4444]",
      Icon: AlertTriangle,
      label: "Urgente",
    },
  };
  const t = TONE[tone];
  const ToneIcon = t.Icon;

  const fmtShortDate = (iso) => {
    try {
      const d = new Date(`${iso}T00:00:00`);
      return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
    } catch {
      return iso;
    }
  };

  return (
    <div
      className={`mt-4 pt-4 border-t border-[#2A3447] space-y-3`}
      data-testid={`credit-health-${accountId}`}
    >
      {/* Semaphore header */}
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
          Salud de tarjeta
        </span>
        <span
          className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full border ${t.border} ${t.bg} ${t.text}`}
          data-testid={`credit-health-tone-${accountId}`}
        >
          <ToneIcon className="w-3 h-3" />
          {t.label}
        </span>
      </div>

      {/* Utilization bar */}
      {status.credit_limit != null && status.credit_limit > 0 && (
        <div>
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="text-[#94A3B8]">Saldo disponible</span>
            <span className="font-numeric text-[#F1F5F9]" data-testid={`credit-available-${accountId}`}>
              {formatCurrency(status.available_credit ?? 0, currency)}{" "}
              <span className="text-[#64748B]">
                / {formatCurrency(status.credit_limit, currency)}
              </span>
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-[#1B2638] overflow-hidden">
            <div
              className={`h-full ${t.bar} transition-all`}
              style={{ width: `${Math.min(100, Math.max(2, util))}%` }}
              data-testid={`credit-utilization-bar-${accountId}`}
            />
          </div>
          <p className="text-[10px] text-[#64748B] mt-1">
            Uso{" "}
            <span className={t.text} data-testid={`credit-utilization-pct-${accountId}`}>
              {util}%
            </span>
          </p>
        </div>
      )}

      {/* Key amounts */}
      {!compact && (
        <div className="grid grid-cols-2 gap-3 pt-1">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#94A3B8]">
              Para no generar intereses
            </p>
            <p
              className="font-numeric text-base font-semibold text-[#F1F5F9] mt-1"
              data-testid={`credit-interest-free-${accountId}`}
            >
              {formatCurrency(interestFree, currency)}
            </p>
            {status.next_payment_date && (
              <p className="text-[10px] text-[#94A3B8] mt-0.5">
                antes del {fmtShortDate(status.next_payment_date)}
              </p>
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#94A3B8]">
              Próximo pago
            </p>
            <p
              className={`font-numeric text-base font-semibold mt-1 ${t.text}`}
              data-testid={`credit-days-to-payment-${accountId}`}
            >
              {daysPay < 0
                ? `${Math.abs(daysPay)} d. atrasado`
                : daysPay === 0
                ? "Hoy"
                : `${daysPay} d.`}
            </p>
            {status.next_cutoff_date && (
              <p className="text-[10px] text-[#94A3B8] mt-0.5">
                Corte: {fmtShortDate(status.next_cutoff_date)}
              </p>
            )}
          </div>
        </div>
      )}
      {/* Premium: Plan de pago inteligente */}
      {!compact && (isPremium ? (
        <div
          className="mt-3 pt-3 border-t border-dashed border-[#2A3447]"
          data-testid={`credit-smart-plan-${accountId}`}
        >
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-[#0052FF]" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-[#0052FF] font-semibold">
              Plan de pago inteligente
            </span>
          </div>
          {planLoading && (
            <div className="flex items-center gap-2 text-[#64748B] text-xs">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Calculando...
            </div>
          )}
          {!planLoading && plan && (
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2 text-[10px]">
                <div className="rounded-lg bg-[#0F172A] p-2 border border-[#2A3447]">
                  <p className="text-[#94A3B8] uppercase tracking-wider">Mínimo</p>
                  <p
                    className="font-numeric text-sm font-semibold text-[#94A3B8] mt-0.5"
                    data-testid={`credit-plan-minimum-${accountId}`}
                  >
                    {formatCurrency(plan.minimum_payment ?? 0, currency)}
                  </p>
                </div>
                <div className="rounded-lg bg-[#0052FF]/15 p-2 border border-[#0052FF]/40">
                  <p className="text-[#0052FF] uppercase tracking-wider">Inteligente</p>
                  <p
                    className="font-numeric text-sm font-semibold text-[#F1F5F9] mt-0.5"
                    data-testid={`credit-plan-smart-${accountId}`}
                  >
                    {formatCurrency(plan.smart_payment ?? 0, currency)}
                  </p>
                </div>
                <div className="rounded-lg bg-[#0F172A] p-2 border border-[#2A3447]">
                  <p className="text-[#94A3B8] uppercase tracking-wider">Sin intereses</p>
                  <p
                    className="font-numeric text-sm font-semibold text-[#F1F5F9] mt-0.5"
                    data-testid={`credit-plan-interest-free-${accountId}`}
                  >
                    {formatCurrency(plan.interest_free_payment ?? 0, currency)}
                  </p>
                </div>
              </div>
              {plan.projected_annual_interest_savings > 0 && (
                <div className="flex items-center gap-1.5 text-[11px] text-[#10B981] mt-1">
                  <TrendingDown className="w-3 h-3" />
                  <span data-testid={`credit-plan-savings-${accountId}`}>
                    Ahorras ~
                    <span className="font-numeric font-semibold">
                      {formatCurrency(plan.projected_annual_interest_savings, currency)}
                    </span>{" "}
                    al año en intereses pagando el monto inteligente.
                  </span>
                </div>
              )}
              <p className="text-[10px] text-[#64748B] leading-relaxed">
                Meta: uso ≤ {plan.smart_target_utilization_pct}% (recomendado por Buró de
                Crédito). Tasa estimada: {plan.annual_interest_rate_pct}% CAT.
              </p>
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowSubscribe(true)}
          className="mt-3 pt-3 border-t border-dashed border-[#2A3447] w-full text-left group"
          data-testid={`credit-smart-plan-locked-${accountId}`}
        >
          <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-[#0052FF]/10 to-[#10B981]/10 border border-[#0052FF]/30 hover:border-[#0052FF]/60 transition-all">
            <div className="flex items-start gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-[#0052FF]/20 flex items-center justify-center shrink-0">
                <Lock className="w-3.5 h-3.5 text-[#0052FF]" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#0052FF] font-semibold flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Plan de pago inteligente
                </p>
                <p className="text-[11px] text-[#94A3B8] mt-0.5 leading-snug">
                  Descubre cuánto pagar para mantener salud crediticia y ahorra hasta miles en intereses al año.
                </p>
              </div>
            </div>
            <span className="text-[10px] text-[#0052FF] font-semibold group-hover:underline shrink-0 ml-2">
              Premium →
            </span>
          </div>
        </button>
      ))}

      <SubscribeDialog open={showSubscribe} onOpenChange={setShowSubscribe} />
    </div>
  );
}
